<script setup lang="ts">
// import { useDishes } from '@/composables/useDishes';
import { useFetch } from '@/composables/useFetch';
import { usePlanning } from '@/composables/usePlanning';
import type { MessageSchema } from '@/i18n';
// import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore';
import { usePlanningStore } from '@/stores/planningStore';
// import { useUserStore } from '@/stores/useUserStore';
import { type Dish, type Ingredient } from '@/types/dish';
import type { MenuCourse } from '@/types/planning';
import { transformAndSortIngredients } from '@/utils/dishes';
import { calculateDishPrice } from '@/utils/prices';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Sidebar from 'primevue/sidebar';
// import { useToast } from 'primevue/usetoast';
import { usePermissions } from '@/composables/usePermissions';
import { useSafeArea } from '@/composables/useSafeArea';
import { computed, onMounted, onUpdated, ref, type ModelRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import DishImage from '../Menus/DishImage.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();
const { bottomSafeArea } = useSafeArea();

// const { categories } = useCategories();
const showSideBar: ModelRef<boolean | undefined> = defineModel('visible', { type: Boolean, required: true });
const sideBarIngredient: ModelRef<Ingredient | undefined> = defineModel('ingredient', { type: Object as () => Ingredient });
// const ingredientCategory = { name: t('orders.ingredientsCategories.other'), value: 'other' }
// const dishesStore = useDishesStore();
// const ingredientUnits = ref(dishesStore.units);
const { fetchData } = useFetch();

const router = useRouter();
// const toast = useToast();
// const user = ref(useUserStore().user);
const planningStore = usePlanningStore();
// const dishesStore = ref(useDishesStore());
const dish = ref<Dish | null>(null);

// const { deleteDish, duplicateDish, toggleFavorite } = useDishes();
const { duplicatePlannedDishAndUpdatePlan } = usePlanning();

const hasImage = computed(() => {
  return dish.value?.image_url_gastro || dish.value?.image_url_bistro || dish.value?.image_url_user;
});

const listIngredients = computed(() => {
  if (!dish.value) return [];
  return transformAndSortIngredients(dish.value.ingredients, dish.value.servings);
});

const showAllDates = ref(false);

const displayedPlannedDishes = computed(() => {
  if (showAllDates.value) {
    return dish.value?.isPlanned || [];
  } else {
    return dish.value?.isPlanned?.slice(0, 3) || [];
  }
});

const edition = ref(false);
const dishName = computed(() => {
  return planningStore.dishToPlan?.clipboardDish.name ?? '';
});

// const emit = defineEmits<{
//   (e: 'closeSidebar'): void;
// }>();

const props = defineProps<{
  // ingredients: Ingredient[]
  menuCourse: MenuCourse;
  plannedMenuName: string;
}>();

onUpdated(() => {
  edition.value = sideBarIngredient.value?.id === -1 ? false : true;
  console.log(dish.value);
});

onMounted(async () => {
  if (props.menuCourse?.dish?.id) {
    const dishId = props.menuCourse.dish.id;
    const { data, error } = await fetchData(`${useEnvStore().apiUrl}/dishes/${dishId}`);

    if (error) {
      console.error('Failed to fetch dish data:', error);
    } else {
      // Update the dish with the fetched data
      // dishesStore.value.selectedDish = data as Dish;
      dish.value = data as Dish;
    }
  }
});

const redirectToDishDetail = () => {
  if (!props.menuCourse?.dish) return;
  const dishId = props.menuCourse?.dish.id; // Assuming dish has an id property
  router.push(`/menu/dishes/${dishId}`);
};

async function openNewDishInstance() {
  if (!dish.value) return;
  const duplicateDish = await duplicatePlannedDishAndUpdatePlan(dish.value, props.menuCourse.id, props.menuCourse.planned_menu_id);
  if (duplicateDish) {
    router.push(`/menu/dishes/${duplicateDish.id}`);
  }
}
</script>
<template>
  <Sidebar
    v-if="dish"
    v-model:visible="showSideBar"
    :header="t('menuDishView.addDishtoWeekMenu', { dishName: dishName })"
    position="bottom"
    style="height: 90%"
    :block-scroll="true"
    :pt="{
      content: { class: 'px-0 pb-6 mt-6 mb-8 h-full w-full grow overflow-y-auto' },
    }"
    class="w-full md:w-3/4 lg:w-1/2"
  >
    <template #header>
      <h2 class="mb-0 w-full">{{ dish.name }}</h2>
    </template>
    <div class="mx-8 mb-8">
      <!-- <div class="flex justify-between gap-6 items-top">
          <h3>
            {{ dish ? dish.name : t('common.loading') }}
          </h3>
          <Button v-if="dish" link rounded :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
            :pt-options="{ mergeProps: true }">
            <template #icon>
              <HeartFull v-if="dish.is_favorite" />
              <HeartEmpty v-else />
            </template>
          </Button>
        </div> -->
      <div v-if="dish && hasImage" class="my-5 flex justify-center">
        <DishImage :dish-id="dish.id" :type="dish.selected_image" :alt="dish.name" />
      </div>
      <div v-if="dish && dish.isPlanned" class="mb-6">
        <h3 class="mb-2">{{ t('menuDishView.planning.plannedOn') }}</h3>
        <ul class="flex flex-col gap-3">
          <li v-for="(plannedDish, index) in displayedPlannedDishes" :key="index" class="cursor-pointer">
            {{
              new Date(plannedDish.date).toLocaleString('fr-CH', {
                dateStyle: 'short',
              })
            }}
            - {{ plannedDish.menu_name }}
          </li>
        </ul>
        <div class="mt-3">
          <p v-if="!showAllDates && dish.isPlanned.length > 3" class="inline-button mb-10" @click="showAllDates = true">
            {{
              t('menuDishView.planning.showMoreDates', {
                count: dish.isPlanned.length - 3,
                s: dish.isPlanned.length - 3 > 1 ? 's' : '',
              })
            }}
          </p>
          <p v-if="showAllDates" class="inline-button mb-10" @click="showAllDates = false">
            {{ t('menuDishView.planning.showLessDates') }}
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-3">
        <p>
          <span class="font-bold">{{ t('profileFavorites.creationForm.numberOfPeople') }}: </span>{{ dish?.servings }}
        </p>
        <DataTable :value="listIngredients" size="small" class="ingredients-list">
          <Column field="quantity" :header="t('profileFavorites.creationForm.quantityShort')">
            <template #body="slotProps">
              {{ slotProps.data.quantity > 0 ? slotProps.data.quantity : '' }}
            </template>
          </Column>
          <Column field="unit" :header="t('profileFavorites.creationForm.unit')" />
          <Column field="name" :header="t('profileFavorites.creationForm.name')" />
          <Column field="price" :header="t('profileFavorites.creationForm.price')" />
        </DataTable>
        <div class="flex flex-col items-end gap-4">
          <!-- <p class="font-bold">~ CHF {{ dish ? calculateDishPrice(dish, numberOfPeople) : 0 }}</p> -->
          <div class="flex w-full justify-between gap-4">
            <span>~ {{ dish?.country?.currency_symbol }} {{ dish ? calculateDishPrice(dish, 1) : 0 }} / {{ t('menuDishView.nbPersonShort') }}</span>
            <span class="font-bold">~ {{ dish?.country?.currency_symbol }} {{ dish ? calculateDishPrice(dish, dish.servings) : 0 }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="can('dishes', 'update')"
      class="custom-shadow fixed inset-x-0 bottom-0 z-50 flex flex-row justify-between gap-6 bg-white px-4 py-3"
      :style="{ paddingBottom: `calc(${bottomSafeArea} + 12px)` }"
    >
      <Button
        v-if="dish && dish.isPlanned && dish.isPlanned.length > 1"
        :label="t('planning.editForThisDate')"
        :disabled="planningStore.dishToPlan?.selectedPlannedMenuIds.length === 0"
        outlined
        @click="openNewDishInstance"
      />
      <Button
        :label="dish && dish.isPlanned && dish.isPlanned.length > 1 ? t('planning.editForAllDates') : t('common.seeMore')"
        :disabled="planningStore.dishToPlan?.selectedPlannedMenuIds.length === 0"
        @click="redirectToDishDetail"
      />
    </div>
    <div
      v-else
      class="custom-shadow fixed inset-x-0 bottom-0 z-50 flex flex-row justify-end gap-6 bg-white px-4 py-3"
      :style="{ paddingBottom: `calc(${bottomSafeArea} + 12px)` }"
    >
      <Button :label="t('orders.viewDish')" :disabled="planningStore.dishToPlan?.selectedPlannedMenuIds.length === 0" @click="redirectToDishDetail" />
    </div>
  </Sidebar>
</template>
